/* NoamResponse — réponse premium : conclusion d'abord, puis descente vers la preuve.
 * Progressive disclosure honnête : on structure l'affichage du texte réel,
 * on n'invente ni sources ni provenance. */
import { useMemo, useState } from "react";
import AiText from "@/components/AiText";
import AgentActionCards from "@/components/AgentActionCards";
import OntologyExplorerButton from "@/components/OntologyExplorerButton";
import { ChevronDown, ChevronUp, Archive, Layers, Network, Globe } from "lucide-react";

const PROVENANCE = [
  { key: "OBSERVED", label: "Observé", re: /observ[eé]/i, color: "#1D9E75" },
  { key: "DERIVED", label: "Dérivé", re: /d[eé]riv[eé]/i, color: "#378ADD" },
  { key: "INFERRED", label: "Inféré", re: /inf[eé]r[eé]/i, color: "#EF9F27" },
];

function splitText(text) {
  if (!text || text.length < 700) return { lead: text || "", rest: "" };
  const heading = text.slice(250).search(/\n#{1,3} /);
  if (heading !== -1) {
    const idx = 250 + heading;
    return { lead: text.slice(0, idx), rest: text.slice(idx + 1) };
  }
  const idx = text.indexOf("\n\n", 400);
  if (idx === -1 || idx > text.length - 150) return { lead: text, rest: "" };
  return { lead: text.slice(0, idx), rest: text.slice(idx + 2) };
}

export default function NoamResponse({ msg, onArchive, archiveLabel }) {
  // P0 2026-09 : réponse TOUJOURS intégralement visible par défaut (parité LLM).
  // L'utilisateur peut replier manuellement — NOAM ne masque jamais sa réponse.
  const [open, setOpen] = useState(true);
  const { lead, rest } = useMemo(() => splitText(msg.text), [msg.text]);
  const provenance = useMemo(
    () => PROVENANCE.filter((p) => p.re.test(msg.text || "")),
    [msg.text]
  );
  const modules = msg.modules_used || [];

  return (
    <div className="max-w-[92%] sm:max-w-[85%]" data-testid={`v2-msg-assistant-${msg.id}`}>
      <div className="rounded-2xl rounded-bl-md border border-white/[0.07] bg-[#111113] px-5 py-4">
        <AiText copyable={false}>{lead}</AiText>

        {rest && (
          <>
            {open && (
              <div className="mt-2 pt-2 border-t border-white/[0.05]">
                <AiText copyable={false}>{rest}</AiText>
              </div>
            )}
            <button onClick={() => setOpen(!open)} aria-expanded={open}
              data-testid={`v2-msg-expand-${msg.id}`}
              className="mt-2.5 inline-flex items-center gap-1.5 text-[11px] text-[#378ADD] hover:text-white transition-colors">
              {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              {open ? "Replier le détail" : "Afficher la suite de la réponse"}
            </button>
          </>
        )}

        {msg.actions && msg.actions.length > 0 && <AgentActionCards actions={msg.actions} />}

        {/* Recherche web autonome : sources citées par NOAM (cliquables) */}
        {msg.web_sources && msg.web_sources.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid={`v2-web-sources-${msg.id}`}>
            <span className="text-[10px] text-zinc-500 inline-flex items-center gap-1">
              <Globe className="w-3 h-3 text-[#378ADD]" /> Recherche web :
            </span>
            {msg.web_sources.map((s) => (
              <a key={s.n} href={s.url} target="_blank" rel="noreferrer"
                data-testid={`v2-web-source-${msg.id}-${s.n}`}
                title={s.url}
                className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border border-[#378ADD]/25 bg-[#378ADD]/10 text-[#8FC1EE] hover:border-[#378ADD]/60 transition-colors max-w-[220px]">
                <span className="font-mono">[{s.n}]</span>
                <span className="truncate">{s.title || s.url}</span>
              </a>
            ))}
          </div>
        )}

        {/* Relations depuis le chat : objets canoniques résolus côté serveur (zéro invention) */}
        {msg.context_objects && msg.context_objects.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid={`v2-context-objects-${msg.id}`}>
            {msg.context_objects.map((co) => (
              <OntologyExplorerButton key={`${co.otype}-${co.oid}`} otype={co.otype} oid={co.oid}
                label={co.label} rootLabel="Conversation"
                className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border border-[#EFD127]/30 bg-[#EFD127]/10 text-[#EFD127] hover:border-[#EFD127]/60 transition-colors">
                <Network className="w-3 h-3" /> Voir {co.label.length > 32 ? co.label.slice(0, 32) + "…" : co.label}
              </OntologyExplorerButton>
            ))}
          </div>
        )}

        {(modules.length > 0 || provenance.length > 0 || (msg.id && msg.id !== "greet" && !String(msg.id).startsWith("err"))) && (
          <div className="mt-3 pt-2.5 border-t border-white/[0.06] flex items-center gap-2 flex-wrap">
            {modules.length > 0 && (
              <span className="inline-flex items-center gap-1.5 text-[10px] text-zinc-500" data-testid={`v2-msg-modules-${msg.id}`}>
                <Layers className="w-3 h-3 text-[#378ADD]" />
                {modules.slice(0, 5).map((m) => (
                  <span key={m} className="px-1.5 py-0.5 rounded-full bg-[#378ADD]/10 text-[#378ADD] border border-[#378ADD]/20 whitespace-nowrap">{m}</span>
                ))}
                {modules.length > 5 && <span>+{modules.length - 5}</span>}
              </span>
            )}
            {provenance.map((p) => (
              <span key={p.key} className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-full border"
                style={{ color: p.color, borderColor: `${p.color}4d`, background: `${p.color}20` }}>
                {p.label}
              </span>
            ))}
            {onArchive && msg.id && msg.id !== "greet" && !String(msg.id).startsWith("err") && (
              <button onClick={() => onArchive(msg)} data-testid={`v2-archive-${msg.id}`}
                className="ml-auto text-[10px] text-zinc-500 hover:text-[#EF9F27] inline-flex items-center gap-1 transition-colors">
                <Archive className="w-3 h-3" /> {archiveLabel}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
